import type { ReactNode } from 'react'
import { lazy } from 'react'
import type { CSSVariableBreakpointToken } from '../types'

const AuthLayout = lazy(() =>
  import('./auth').then((mod) => ({ default: mod.AuthLayout })),
)
const DashboardLayout = lazy(() =>
  import('./dashboard').then((mod) => ({ default: mod.DashboardLayout })),
)
const DefaultLayout = lazy(() =>
  import('./default').then((mod) => ({ default: mod.DefaultLayout })),
)
const LandingLayout = lazy(() =>
  import('./landing').then((mod) => ({ default: mod.LandingLayout })),
)
const DashboardReverseLayout = lazy(() =>
  import('./dashboard-reverse').then((mod) => ({
    default: mod.DashboardReverseLayout,
  })),
)
const DashboardProfileLayout = lazy(() =>
  import('./dashboard-profile').then((mod) => ({
    default: mod.DashboardProfileLayout,
  })),
)
const DashboardAgentsLayout = lazy(() =>
  import('./dashboard-agents').then((mod) => ({
    default: mod.DashboardAgentsLayout,
  })),
)

export type LayoutType =
  | 'default'
  | 'auth'
  | 'dashboard'
  | 'dashboard-agent'
  | 'landing'
  | 'dashboard-reverse'
  | 'dashboard-profile'

interface ILayoutStrategy {
  render: ({
    children,
    footer,
  }: {
    children: ReactNode
    footer?: ReactNode
    header?: ReactNode
    sidebar?: ReactNode
    subBgHeader?: ReactNode
    subHeader?: ReactNode
    screenMaxWidth?: CSSVariableBreakpointToken
    mainClassName?: string
    asideMaxWidth?: CSSVariableBreakpointToken
  }) => ReactNode
}

const layoutStrategies: Record<LayoutType, ILayoutStrategy> = {
  default: {
    render: (props) => <DefaultLayout {...props} />,
  },
  auth: {
    render: (props) => <AuthLayout {...props} />,
  },
  dashboard: {
    render: (props) => <DashboardLayout {...props} />,
  },
  'dashboard-reverse': {
    render: (props) => <DashboardReverseLayout {...props} />,
  },
  'dashboard-profile': {
    render: (props) => <DashboardProfileLayout {...props} />,
  },
  'dashboard-agent': {
    render: (props) => <DashboardAgentsLayout {...props} />,
  },
  landing: {
    render: (props) => <LandingLayout {...props} />,
  },
}

interface ILayoutProps {
  type?: LayoutType
  children: ReactNode
  footer?: ReactNode
  header?: ReactNode
  sidebar?: ReactNode
  subHeader?: ReactNode
  subBgHeader?: ReactNode
  screenMaxWidth?: CSSVariableBreakpointToken
  mainClassName?: string
  asideMaxWidth?: CSSVariableBreakpointToken
}

export const Layout = ({
  type = 'default',
  children,
  footer,
  header,
  sidebar,
  subHeader,
  subBgHeader,
  screenMaxWidth,
  mainClassName,
  asideMaxWidth,
}: ILayoutProps) => {
  const strategy = layoutStrategies[type]

  return strategy.render({
    children,
    footer,
    header,
    sidebar,
    subHeader,
    subBgHeader,
    screenMaxWidth,
    mainClassName,
    asideMaxWidth,
  })
}
